import {type RefObject, useState} from "react";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX} from "./campaignSpace";
import {campaignFit, fittingRecipientCardHeight, recipientGiftFits, recipientGiftReserve, RECIPIENT_CAMPAIGN_SLOT_HEIGHT_PX} from "./campaignFit";

/** Observe the existing card, including fixed content changes while its outer height is capped. */
export function useRecipientCampaignFit(rootRef: RefObject<HTMLElement | null>, enabled: boolean, onGiftFit?: (visible: boolean) => void): boolean {
	const [fits, setFits] = useState(false);
	useIsomorphicLayoutEffect(() => {
		const root = rootRef.current;
		if (!enabled || !root) { setFits(false); onGiftFit?.(false); return; }
		const measure = () => {
			const card = root.querySelector<HTMLElement>("[data-testid='recipient-card']");
			if (!card) { setFits(false); onGiftFit?.(false); return; }
			const list = card.querySelector<HTMLElement>("[data-testid='recipient-file-list']");
			const cardStyle = getComputedStyle(card);
			const rootStyle = getComputedStyle(root);
			const contentHeight = Math.max(card.clientHeight, card.scrollHeight)
				+ parseFloat(cardStyle.borderTopWidth) + parseFloat(cardStyle.borderBottomWidth);
			const listHeight = list?.offsetHeight ?? 0;
			/*
			 * What the list would be at its natural height. It is the one box on the card that scrolls,
			 * so its rendered height says nothing about how much of it there is, and a list SHORTER than
			 * its own floor is what keeps a one-file card from reserving room it has no rows for.
			 */
			const listContentHeight = list?.scrollHeight ?? 0;
			/*
			 * Two different heights, deliberately (owner 2026-09-10, card clipped during the tuck):
			 *
			 * WOULD-NEED — the taller accessory's own height. This feeds the FIT decision, and must
			 * ignore rows entirely: the not-fit state absolutely-positions a hidden accessory, which
			 * empties its row, so a row-aware fit measure could never see the gift becoming viable
			 * again. `max` rather than a sum because at rest only one accessory is ever granted
			 * space — during a tuck the leaving tag and the arriving pull briefly coexist, and
			 * summing them flipped the fit off mid-animation.
			 *
			 * OCCUPIED — per accessory, the smaller of its own height and its fr-track row's height.
			 * This feeds the card's max-height reserve. During a tuck the tag deliberately overflows
			 * its CLOSING row at full height while riding up behind the card, so its own height lies;
			 * the row is what actually still takes frame height, and it animates, so the card grows
			 * back at the tuck's own pace instead of being clipped by a frozen reserve. A pinned
			 * accessory has no row and keeps the plain measure.
			 */
			const occupancyOf = (accessory: HTMLElement | null) => {
				if (!accessory) return 0;
				const row = accessory.closest<HTMLElement>("[data-peel-row]");
				return row ? Math.min(accessory.offsetHeight, row.offsetHeight) : accessory.offsetHeight;
			};
			const tag = root.querySelector<HTMLElement>("[data-peel-position]");
			const pull = root.querySelector<HTMLElement>("[data-peel-pulls]");
			const tagHeight = Math.max(tag?.offsetHeight ?? 0, pull?.offsetHeight ?? 0);
			const occupiedHeight = occupancyOf(tag) + occupancyOf(pull);
			const headerClearance = parseFloat(rootStyle.getPropertyValue("--recipient-header-clearance"));
			const hero = root.querySelector("[data-campaign-layout]")?.parentElement;
			/*
			 * The hero's bottom padding is the WHOLE bottom inset since ui#2457, cookie reserve
			 * included: both clearances moved off the surface onto the hero so the campaign's art
			 * could fill the window (`CampaignSurface.styles.ts`). The surface's own padding-bottom
			 * carried half of it and was summed in here; it is 0 now, and a term that can only be
			 * zero reads as a second source of the number rather than as part of it.
			 */
			const cookieReserve = hero ? parseFloat(getComputedStyle(hero).paddingBottom) : 0;
			// On a phone the gift waits as a tab in a row laid out beside the card, which opens it in a
			// dialog (ui#3372); that row is out of the flow and asks the card for no room.
			const docked = root.querySelector("[data-peel-beside]") !== null;
			const giftFits = recipientGiftFits({viewportHeight: window.innerHeight, contentHeight,
				listHeight, listContentHeight, tagHeight, headerClearance, cookieReserve, docked});
			const giftReserve = recipientGiftReserve({fits: giftFits, docked, occupiedHeight});
			root.toggleAttribute("data-recipient-gift-fit", giftFits);
			onGiftFit?.(giftFits);
			root.style.setProperty("--recipient-gift-reserve", `${giftReserve}px`);
			/*
			 * The card's floor is NOT published to its stylesheet any more (ui#2457, later).
			 *
			 * It was, as --recipient-card-floor-h, and the card wore it as a min-height so the page could
			 * scroll for whatever would not fit. The owner reversed that: the page is fixed, so a floor that
			 * can exceed the column is exactly what must not reach the CSS. The card is capped by its column
			 * and scrolls itself as the last resort instead. The same number is still computed, for the
			 * CAMPAIGN and the gift below, which ask how much room the card needs rather than force it to
			 * take it.
			 */
			setFits(campaignFit({
				sideBySide: window.matchMedia(`(min-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX}px)`).matches,
				viewportHeight: window.innerHeight,
				fittingCardHeight:
					fittingRecipientCardHeight(contentHeight, listHeight, listContentHeight) + giftReserve,
				headerClearance,
				cookieReserve,
				minimumCampaignSlotHeight: RECIPIENT_CAMPAIGN_SLOT_HEIGHT_PX,
			}));
		};
		const resize = new ResizeObserver(measure);
		const syncCard = () => {
			resize.disconnect();
			const card = root.querySelector("[data-testid='recipient-card']");
			if (card) {
				resize.observe(card);
				for (const child of card.children) resize.observe(child);
			}
			/* The rows too: their fr-track heights are what the reserve now reads, and they animate. */
			for (const accessory of root.querySelectorAll("[data-peel-row], [data-peel-position], [data-peel-pulls]")) resize.observe(accessory);
			measure();
		};
		syncCard();
		const content = new MutationObserver(syncCard);
		content.observe(root, {childList: true, subtree: true, characterData: true});
		const documentStyle = new MutationObserver(measure);
		documentStyle.observe(document.documentElement, {attributes: true, attributeFilter: ["style", "data-theme"]});
		window.addEventListener("resize", measure);
		return () => { resize.disconnect(); content.disconnect(); documentStyle.disconnect(); window.removeEventListener("resize", measure); };
	}, [rootRef, enabled, onGiftFit]);
	return fits;
}
